import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Award, Briefcase, BookOpen, Users, CheckCircle2, ArrowLeft } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, PageLoader, StatusBadge } from "@/components/common";
import { RequestDialog } from "@/components/RequestDialog";

export default function TutorProfile({ backTo }) {
  const { id } = useParams();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [err, setErr] = useState("");
  const [reqStatus, setReqStatus] = useState(null);
  const [open, setOpen] = useState(false);

  const loadStatus = () => {
    if (user?.role === "student") api.get(`/student/tutor-status/${id}`).then((r) => setReqStatus(r.data.request));
  };
  useEffect(() => {
    api.get(`/tutors/${id}`).then((r) => setT(r.data)).catch(() => setErr("Tutor not found"));
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  if (err) return <p className="p-10 text-slate-500">{err}</p>;
  if (!t) return <PageLoader />;
  const blocked = reqStatus && ["pending", "approved"].includes(reqStatus.status);
  return (
    <div className="mx-auto max-w-5xl px-0 py-6 sm:py-10">
      <Link to={backTo} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#1F6FFF]" data-testid="tutor-profile-back"><ArrowLeft className="h-4 w-4" />Back to tutors</Link>
      <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white">
        <div className="grid-texture relative h-36 bg-[#0B1A33]" />
        <div className="px-6 pb-8 sm:px-10">
          <div className="-mt-16 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-5">
              {t.profile_picture ? <img src={fileUrl(t.profile_picture)} alt={t.name} className="h-32 w-32 rounded-2xl border-4 border-white object-cover" />
                : <Avatar name={t.name} size="h-32 w-32" className="rounded-2xl border-4 border-white text-3xl" />}
              <div className="pb-1">
                <h1 className="text-2xl font-extrabold text-[#0B1A33] sm:text-3xl" data-testid="tutor-profile-name">{t.name}</h1>
                <p className="font-medium text-[#1F6FFF]">{t.subject} · {t.specialization}</p>
              </div>
            </div>
            {reqStatus && <div data-testid="tutor-profile-request-status">Request: <StatusBadge status={reqStatus.status} /></div>}
            <button onClick={() => setOpen(true)} disabled={blocked} data-testid="tutor-profile-request-button"
              className="rounded-full bg-[#1F6FFF] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#1450C9] disabled:cursor-not-allowed disabled:bg-slate-300">
              {blocked ? (reqStatus.status === "approved" ? "Approved tutor" : "Request pending") : "Request Session"}
            </button>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-4">
            {[[Award, "Qualification", t.qualification], [Briefcase, "Experience", `${t.experience} years`],
              [CheckCircle2, "Sessions taught", t.completed_sessions], [Users, "Students", t.students_count]].map(([I, l, v]) => (
              <div key={l} className="rounded-xl bg-[#F4F7FA] p-4">
                <I className="h-5 w-5 text-[#3CC34A]" />
                <p className="mt-2 text-xs uppercase tracking-wider text-slate-500">{l}</p>
                <p className="font-semibold text-[#0B1A33]">{v}</p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <h3 className="flex items-center gap-2 text-lg font-bold text-[#0B1A33]"><BookOpen className="h-5 w-5 text-[#1F6FFF]" />About</h3>
            <p className="mt-2 leading-relaxed text-slate-600" data-testid="tutor-profile-bio">{t.bio || "This tutor hasn't added a bio yet."}</p>
          </div>
        </div>
      </div>
      <RequestDialog tutor={open ? t : null} onClose={() => setOpen(false)} onDone={loadStatus} />
    </div>
  );
}
